import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { featuredProductAPI } from '../../api'
import type { FeaturedProductFormData } from '../../types'

// Query key factory for featured products
export const featuredProductKeys = {
  all: ['featuredProducts'] as const,
  list: () => [...featuredProductKeys.all, 'list'] as const,
  publicList: () => [...featuredProductKeys.all, 'public'] as const,
  detail: (slotNumber: number) => [...featuredProductKeys.all, 'detail', slotNumber] as const,
}

// Fetch all featured product slots (admin)
export function useFeaturedProducts(adminView = true) {
  return useQuery({
    queryKey: featuredProductKeys.list(),
    queryFn: () => featuredProductAPI.getFeaturedProducts(),
    // In admin, keep fresh data (refetch when tab regains focus)
    staleTime: adminView ? 0 : 5 * 60 * 1000,
    refetchOnWindowFocus: adminView,
  })
}

// Fetch public featured products
export function usePublicFeaturedProducts() {
  return useQuery({
    queryKey: featuredProductKeys.publicList(),
    queryFn: () => featuredProductAPI.getPublicFeaturedProducts(),
    // Always refetch - staleTime 0 means data is always considered stale
    staleTime: 0,
  })
}

// Fetch single featured product slot
export function useFeaturedProduct(slotNumber: number | null) {
  return useQuery({
    queryKey: featuredProductKeys.detail(slotNumber!),
    queryFn: () => featuredProductAPI.getFeaturedProduct(slotNumber!),
    enabled: slotNumber !== null,
  })
}

// Update featured product slot mutation
export function useUpdateFeaturedProduct() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ slotNumber, data }: { slotNumber: number; data: Partial<FeaturedProductFormData> }) =>
      featuredProductAPI.updateFeaturedProduct(slotNumber, data),
    onSuccess: (data) => {
      // Update the specific slot in cache
      queryClient.setQueryData(featuredProductKeys.detail(data.slot.slot_number), { slot: data.slot })
      // Invalidate lists to reflect changes
      queryClient.invalidateQueries({ queryKey: featuredProductKeys.list() })
      queryClient.invalidateQueries({ queryKey: featuredProductKeys.publicList() })
    },
  })
}

// Reorder featured product slots mutation
export function useReorderFeaturedProducts() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (slotNumbers: number[]) => featuredProductAPI.reorderFeaturedProducts(slotNumbers),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: featuredProductKeys.list() })
      queryClient.invalidateQueries({ queryKey: featuredProductKeys.publicList() })
    },
  })
}

// Clear featured product slot mutation
export function useClearFeaturedProduct() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (slotNumber: number) => featuredProductAPI.clearFeaturedProduct(slotNumber),
    onSuccess: (_data, slotNumber) => {
      // Remove the slot from detail cache
      queryClient.removeQueries({ queryKey: featuredProductKeys.detail(slotNumber) })
      // Invalidate lists
      queryClient.invalidateQueries({ queryKey: featuredProductKeys.list() })
      queryClient.invalidateQueries({ queryKey: featuredProductKeys.publicList() })
    },
  })
}
